import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function Diagnostics() {
  const [activeTab, setActiveTab] = useState("overview");
  const [systemStatus, setSystemStatus] = useState(null);
  const [activityLog, setActivityLog] = useState([]);
  const [externalApiLog, setExternalApiLog] = useState(null);
  const [logFilter, setLogFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statusRes, activityRes, apiRes] = await Promise.all([
        axios.get(`${API}/api/admin/diagnostics/system-status`, { headers }),
        axios.get(`${API}/api/admin/diagnostics/activity-log?limit=100&event_filter=${logFilter}`, { headers }),
        axios.get(`${API}/api/admin/diagnostics/external-api-log?limit=50`, { headers }),
      ]);
      setSystemStatus(statusRes.data);
      setActivityLog(activityRes.data.events || []);
      setExternalApiLog(apiRes.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error("Diagnostics fetch error:", err);
    }
    setLoading(false);
  }, [logFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  const formatTime = (ts) => {
    if (!ts) return "N/A";
    const d = new Date(ts);
    return d.toLocaleString();
  };

  const timeSince = (ts) => {
    if (!ts) return "";
    const seconds = Math.floor((new Date() - new Date(ts)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const getEventColor = (type) => {
    const colors = {
      login_success: "#059669",
      login_failed: "#dc2626",
      api_access_success: "#2563eb",
      api_invalid_key: "#dc2626",
      api_rate_limit_exceeded: "#d97706",
    };
    return colors[type] || "#6b7280";
  };

  const getEventLabel = (type) => {
    const labels = {
      login_success: "LOGIN OK",
      login_failed: "LOGIN FAIL",
      api_access_success: "API OK",
      api_invalid_key: "API BAD KEY",
      api_rate_limit_exceeded: "API RATE LIMIT",
    };
    return labels[type] || type;
  };

  const getSeverityColor = (sev) => {
    const c = { critical: "#dc2626", high: "#ea580c", medium: "#d97706", low: "#2563eb", info: "#6b7280" };
    return c[sev] || "#6b7280";
  };

  return (
    <div data-testid="diagnostics-page" style={{ padding: "24px", maxWidth: 1280, margin: "0 auto", fontFamily: "'Inter', sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <div>
          <h1 data-testid="diagnostics-title" style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 }}>System Diagnostics</h1>
          <p style={{ color: "#6b7280", fontSize: 14, margin: "4px 0 0" }}>
            Real-time monitoring and activity logs
            {lastRefresh && <span> &middot; Last refreshed: {formatTime(lastRefresh)}</span>}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <label data-testid="auto-refresh-toggle" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#374151", cursor: "pointer" }}>
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
            Auto-refresh (10s)
          </label>
          <button
            data-testid="refresh-btn"
            onClick={fetchData}
            disabled={loading}
            style={{
              padding: "8px 16px", borderRadius: 6, border: "1px solid #d1d5db",
              background: loading ? "#f3f4f6" : "#fff", cursor: loading ? "default" : "pointer",
              fontSize: 13, fontWeight: 500
            }}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div data-testid="diagnostics-tabs" style={{ display: "flex", gap: 0, borderBottom: "2px solid #e5e7eb", marginBottom: 24 }}>
        {[
          { id: "overview", label: "System Overview" },
          { id: "activity", label: "Activity Log" },
          { id: "external", label: "External API" },
        ].map((tab) => (
          <button
            key={tab.id}
            data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 20px", border: "none", background: "none", cursor: "pointer",
              fontSize: 14, fontWeight: activeTab === tab.id ? 600 : 400,
              color: activeTab === tab.id ? "#1d4ed8" : "#6b7280",
              borderBottom: activeTab === tab.id ? "2px solid #1d4ed8" : "2px solid transparent",
              marginBottom: -2
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && systemStatus && (
        <div data-testid="overview-panel">
          {/* Status Cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 24 }}>
            <StatusCard
              testId="db-status-card"
              title="Database"
              value={systemStatus.database?.status === "connected" ? "Connected" : "Error"}
              color={systemStatus.database?.status === "connected" ? "#059669" : "#dc2626"}
              subtitle={`${systemStatus.collections?.pl_line_items || 0} financial records`}
            />
            <StatusCard
              testId="logins-24h-card"
              title="Logins (24h)"
              value={`${systemStatus.last_24h?.login_success || 0} OK / ${systemStatus.last_24h?.login_failed || 0} Failed`}
              color={systemStatus.last_24h?.login_failed > 5 ? "#dc2626" : "#059669"}
              subtitle={`${systemStatus.collections?.users || 0} total users`}
            />
            <StatusCard
              testId="api-24h-card"
              title="External API (24h)"
              value={`${systemStatus.last_24h?.external_api_success || 0} OK / ${systemStatus.last_24h?.external_api_failed || 0} Failed`}
              color={systemStatus.last_24h?.external_api_failed > 0 ? "#d97706" : "#2563eb"}
              subtitle="Calls from other app"
            />
            <StatusCard
              testId="config-card"
              title="Security Config"
              value={`JWT: ${systemStatus.config?.jwt_expiry_hours || "?"}h`}
              color="#6b7280"
              subtitle={`Login limit: ${systemStatus.config?.login_max_attempts || "?"}/${systemStatus.config?.login_window_seconds || "?"}s`}
            />
          </div>

          {/* Rate Limit Status */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 20, marginBottom: 16 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: "0 0 12px", color: "#111827" }}>Active Rate Limits</h3>
            {Object.keys(systemStatus.rate_limits?.login || {}).length === 0 &&
             Object.keys(systemStatus.rate_limits?.external_api || {}).length === 0 ? (
              <p data-testid="no-rate-limits" style={{ color: "#6b7280", fontSize: 14, margin: 0 }}>No active rate limits - all clear</p>
            ) : (
              <div style={{ fontSize: 13 }}>
                {Object.entries(systemStatus.rate_limits?.login || {}).map(([email, info]) => (
                  <div data-testid={`rate-limit-${email}`} key={email} style={{
                    padding: "8px 12px", background: info.blocked ? "#fef2f2" : "#fffbeb",
                    borderRadius: 6, marginBottom: 4, display: "flex", justifyContent: "space-between"
                  }}>
                    <span><strong>Login:</strong> {email}</span>
                    <span>{info.attempts}/{info.max} attempts {info.blocked && <span style={{ color: "#dc2626", fontWeight: 600 }}> BLOCKED</span>} (resets in {info.resets_in_seconds}s)</span>
                  </div>
                ))}
                {Object.entries(systemStatus.rate_limits?.external_api || {}).map(([ip, info]) => (
                  <div data-testid={`rate-limit-api-${ip}`} key={ip} style={{
                    padding: "8px 12px", background: "#eff6ff", borderRadius: 6, marginBottom: 4,
                    display: "flex", justifyContent: "space-between"
                  }}>
                    <span><strong>API:</strong> {ip}</span>
                    <span>{info.calls} calls in window</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Server Info */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 20 }}>
            <h3 style={{ fontSize: 16, fontWeight: 600, margin: "0 0 12px", color: "#111827" }}>System Info</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 13, color: "#374151" }}>
              <div>Server Time: <strong>{formatTime(systemStatus.server_time)}</strong></div>
              <div>Security Events: <strong>{systemStatus.collections?.security_events || 0}</strong></div>
              <div>Financial Records: <strong>{systemStatus.collections?.pl_line_items || 0}</strong></div>
              <div>Total Users: <strong>{systemStatus.collections?.users || 0}</strong></div>
            </div>
          </div>
        </div>
      )}

      {/* Activity Log Tab */}
      {activeTab === "activity" && (
        <div data-testid="activity-panel">
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            {["all", "logins", "external_api", "errors"].map((f) => (
              <button
                key={f}
                data-testid={`filter-${f}`}
                onClick={() => setLogFilter(f)}
                style={{
                  padding: "6px 14px", borderRadius: 20, border: "1px solid",
                  borderColor: logFilter === f ? "#1d4ed8" : "#d1d5db",
                  background: logFilter === f ? "#eff6ff" : "#fff",
                  color: logFilter === f ? "#1d4ed8" : "#6b7280",
                  fontSize: 13, fontWeight: 500, cursor: "pointer"
                }}
              >
                {f === "all" ? "All Events" : f === "logins" ? "Logins" : f === "external_api" ? "External API" : "Errors"}
              </button>
            ))}
          </div>

          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
            <table data-testid="activity-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Time</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Type</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600, color: "#374151" }}>User/Source</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600, color: "#374151" }}>IP</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Details</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600, color: "#374151" }}>Severity</th>
                </tr>
              </thead>
              <tbody>
                {activityLog.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "#9ca3af" }}>No events found</td></tr>
                ) : (
                  activityLog.map((event, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "8px 12px", whiteSpace: "nowrap", color: "#6b7280" }}>
                        <div>{timeSince(event.timestamp)}</div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>{formatTime(event.timestamp)}</div>
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{
                          display: "inline-block", padding: "2px 8px", borderRadius: 4,
                          fontSize: 11, fontWeight: 600, color: "#fff",
                          background: getEventColor(event.event_type)
                        }}>
                          {getEventLabel(event.event_type)}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", color: "#374151" }}>{event.user_email || "-"}</td>
                      <td style={{ padding: "8px 12px", color: "#6b7280", fontFamily: "monospace", fontSize: 12 }}>{event.ip_address || "-"}</td>
                      <td style={{ padding: "8px 12px", color: "#374151", maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis" }}>{event.details || "-"}</td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{ color: getSeverityColor(event.severity), fontWeight: 500, fontSize: 12 }}>
                          {event.severity || "-"}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <p style={{ color: "#9ca3af", fontSize: 12, marginTop: 8 }}>Showing last {activityLog.length} events</p>
        </div>
      )}

      {/* External API Tab */}
      {activeTab === "external" && externalApiLog && (
        <div data-testid="external-api-panel">
          {/* Summary */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 16, marginBottom: 20 }}>
            <StatusCard testId="api-total" title="Total Calls" value={externalApiLog.summary?.total || 0} color="#374151" subtitle="Recent calls logged" />
            <StatusCard testId="api-success" title="Successful" value={externalApiLog.summary?.successful || 0} color="#059669" subtitle="Valid API key" />
            <StatusCard testId="api-failed" title="Failed" value={externalApiLog.summary?.failed || 0} color="#dc2626" subtitle="Invalid key / rate limited" />
            <StatusCard testId="api-ips" title="Unique IPs" value={externalApiLog.summary?.unique_ips?.length || 0} color="#2563eb" subtitle={externalApiLog.summary?.unique_ips?.join(", ") || "None"} />
          </div>

          {/* IP List */}
          {externalApiLog.summary?.unique_ips?.length > 0 && (
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: 16, marginBottom: 16 }}>
              <h4 style={{ margin: "0 0 8px", fontSize: 14, color: "#166534" }}>Known API Client IPs</h4>
              <div style={{ fontFamily: "monospace", fontSize: 13, color: "#15803d" }}>
                {externalApiLog.summary.unique_ips.map((ip, i) => (
                  <span key={i} style={{ display: "inline-block", background: "#dcfce7", padding: "2px 10px", borderRadius: 4, margin: "2px 4px" }}>{ip}</span>
                ))}
              </div>
            </div>
          )}

          {/* Recent Calls Table */}
          <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
            <table data-testid="external-api-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600 }}>Time</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600 }}>Status</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600 }}>IP Address</th>
                  <th style={{ textAlign: "left", padding: "10px 12px", fontWeight: 600 }}>Details</th>
                </tr>
              </thead>
              <tbody>
                {(externalApiLog.events || []).length === 0 ? (
                  <tr><td colSpan={4} style={{ padding: 32, textAlign: "center", color: "#9ca3af" }}>
                    No external API calls recorded yet. When your other application makes requests, they will appear here.
                  </td></tr>
                ) : (
                  (externalApiLog.events || []).map((event, i) => (
                    <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "8px 12px", color: "#6b7280" }}>
                        <div>{timeSince(event.timestamp)}</div>
                        <div style={{ fontSize: 11, color: "#9ca3af" }}>{formatTime(event.timestamp)}</div>
                      </td>
                      <td style={{ padding: "8px 12px" }}>
                        <span style={{
                          display: "inline-block", padding: "2px 8px", borderRadius: 4,
                          fontSize: 11, fontWeight: 600, color: "#fff",
                          background: getEventColor(event.event_type)
                        }}>
                          {getEventLabel(event.event_type)}
                        </span>
                      </td>
                      <td style={{ padding: "8px 12px", fontFamily: "monospace", fontSize: 12 }}>{event.ip_address || "-"}</td>
                      <td style={{ padding: "8px 12px", color: "#374151" }}>{event.details || "-"}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusCard({ testId, title, value, color, subtitle }) {
  return (
    <div data-testid={testId} style={{
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8,
      padding: 16, borderLeft: `4px solid ${color}`
    }}>
      <div style={{ fontSize: 12, fontWeight: 500, color: "#6b7280", marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{value}</div>
      {subtitle && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>{subtitle}</div>}
    </div>
  );
}

export default Diagnostics;
